import {
  type ApplicationStep,
  type CreateApplicationBody,
  type DecisionStrategyResult,
  type MainCheckCompletedSseEvent,
  type SubmissionResponse,
  type SubmitApplicationAcceptedResponse,
  SseEvent,
} from '@sparrow-card/sparrow-card-constants';
import Api, { FetchMethodType } from 'api/Api';
import {
  getApplicationSseSession,
  waitForApplicationSseEvent,
} from 'utils/sse/applicationSseSession';
import { SseError } from 'utils/sse/SseError';

export type SubmitApplicationRequestBody = CreateApplicationBody;

export type SubmitApplicationResponse = SubmissionResponse | SubmitApplicationAcceptedResponse;

interface IApplicationApi {
  submitApplication(
    applicationData: SubmitApplicationRequestBody,
  ): Promise<SubmitApplicationResponse>;
}

const isCompletedCheckSseEvent = (sseEvent: {
  event: string;
}): sseEvent is MainCheckCompletedSseEvent => sseEvent.event === SseEvent.MainCheckCompleted;

export default class ApplicationApi extends Api implements IApplicationApi {
  public async submitApplication(applicationData: SubmitApplicationRequestBody) {
    return this.fetch<SubmitApplicationResponse>('/submissions/application', {
      method: FetchMethodType.POST,
      body: applicationData,
    });
  }

  public async waitForMainCheck(submissionId: string): Promise<DecisionStrategyResult> {
    const session = getApplicationSseSession();

    if (!session || session.submissionId !== submissionId) {
      throw new SseError('no active application session for main check');
    }

    const sseEvent = await waitForApplicationSseEvent({
      resolveEvents: [SseEvent.MainCheckCompleted, SseEvent.MainCheckFailed],
      closeOnEvents: [SseEvent.MainCheckCompleted, SseEvent.MainCheckFailed],
    });

    if (sseEvent.event === SseEvent.MainCheckFailed) {
      return { passed: false, error: true };
    }

    if (isCompletedCheckSseEvent(sseEvent)) {
      return sseEvent.payload.result;
    }

    throw new SseError('unexpected event payload');
  }

  public getApplicationStep(
    applicationId: string,
  ): Promise<{ step: ApplicationStep; resendAttemptsLeft?: number }> {
    return this.fetch<{ step: ApplicationStep }>(`/application-progress/step/${applicationId}`, {
      method: FetchMethodType.GET,
    });
  }
}
